// /app/frontend/src/pages/risk/LIAWorkflowPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Progress } from '../../components/ui/progress';
import {
  ChevronLeft, ChevronRight, Save, CheckCircle2, Scale,
  Target, Gauge, Shield, Eye, ThumbsUp, ThumbsDown, Minus, Loader2, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { liaAPI, entitiesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// ─── Step definitions ────────────────────────────────────────────────────────
const STEPS = [
  { id: 0, title: 'Assessment Setup', icon: FileText, description: 'Title and process details' },
  { id: 1, title: 'Part 1 — Purpose Test', icon: Target, description: 'Why you want to process the data' },
  { id: 2, title: 'Part 2 — Necessity Test', icon: Gauge, description: 'Whether processing is necessary' },
  { id: 3, title: 'Part 3a — Nature of Data', icon: Shield, description: 'What kind of data is involved' },
  { id: 4, title: 'Part 3b — Reasonable Expectations', icon: Eye, description: 'What individuals would expect' },
  { id: 5, title: 'Part 3c — Likely Impact', icon: Scale, description: 'Impact on individuals\' rights' },
  { id: 6, title: 'Part 4 — DPO Decision', icon: CheckCircle2, description: 'Final decision by DPO' },
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'approved', label: 'Approved — Can Rely on LI' },
  { value: 'rejected', label: 'Rejected — Cannot Rely on LI' },
  { value: 'conditional', label: 'Conditional Approval' },
];

const STATUS_COLORS = {
  draft: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
  in_progress: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  under_review: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  approved: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  rejected: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
  conditional: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
};

const EMPTY_FORM = {
  entity_id: '',
  title: '',
  process_description: '',
  status: 'draft',
  // Part 1
  purpose_why: '', purpose_benefit: '', purpose_third_party_benefit: '',
  purpose_public_benefit: '', purpose_importance: '', purpose_impact_without: '',
  purpose_other_laws: '', purpose_guidelines: '',
  // Part 2
  necessity_helps_purpose: '', necessity_proportionate: '',
  necessity_alternative: '', necessity_less_data: '',
  // Part 3a
  nature_special_category: '', nature_private: '',
  nature_vulnerable: '', nature_capacity: '',
  // Part 3b
  expect_existing_relationship: '', expect_relationship_nature: '',
  expect_collected_directly: '', expect_told_at_time: '',
  expect_how_long_ago: '', expect_tech_changes: '',
  expect_widely_understood: '', expect_new_innovative: '', expect_other_factors: '',
  // Part 3c
  impact_possible: '', impact_control_loss: '', impact_likelihood_severity: '',
  impact_objections: '', impact_happy_explain: '', impact_safeguards: '', impact_opt_out: '',
  // Part 4
  dpo_can_rely: '', dpo_rationale: '', dpo_conditions: '',
  completed_by: '', completion_date: '', comments: '', dpo_name: '',
};

// ─── Reusable question field ──────────────────────────────────────────────────
function QuestionField({ label, guidance, field, value, onChange, rows = 3 }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-slate-200">{label}</Label>
      {guidance && <p className="text-xs text-slate-500 italic">{guidance}</p>}
      <Textarea
        value={value}
        onChange={(e) => onChange(field, e.target.value)}
        placeholder="Enter your response..."
        rows={rows}
        className="bg-slate-900 border-slate-700 text-slate-200 placeholder:text-slate-600 resize-none focus:border-violet-500"
      />
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function LIAWorkflowPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isComplianceReadOnly: isReadOnly } = useAuth();
  const isNew = !id;

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [entities, setEntities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [liaRef, setLiaRef] = useState('');

  // Load existing record
  useEffect(() => {
    entitiesAPI.getAll().then(r => setEntities(r.data)).catch(() => {});
    if (!isNew) {
      liaAPI.getOne(id).then(r => {
        setForm({ ...EMPTY_FORM, ...r.data });
        setLiaRef(r.data.lia_reference || '');
      }).catch(() => {
        toast.error('LIA not found');
        navigate('/risk/assessments');
      }).finally(() => setLoading(false));
    }
  }, [id, isNew, navigate]);

  const handleChange = useCallback((field, value) => {
    if (isReadOnly) return;
    setForm(prev => ({ ...prev, [field]: value }));
  }, [isReadOnly]);

  const handleSave = useCallback(async (finalStatus) => {
    setSaving(true);
    try {
      const payload = { ...form, status: finalStatus || form.status };
      let result;
      if (isNew) {
        result = await liaAPI.create(payload);
        setLiaRef(result.data.lia_reference);
        toast.success(`LIA ${result.data.lia_reference} created`);
        navigate(`/risk/lia/${result.data.id}`, { replace: true });
      } else {
        result = await liaAPI.update(id, payload);
        toast.success('LIA saved');
      }
      setForm(prev => ({ ...prev, ...result.data }));
    } catch {
      toast.error('Failed to save LIA');
    } finally {
      setSaving(false);
    }
  }, [form, id, isNew, navigate]);

  const progress = Math.round(((step + 1) / STEPS.length) * 100);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="h-8 w-8 text-violet-400 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* ── Header ── */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/risk/assessments')} className="text-slate-400">
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-white">Legitimate Interest Assessment</h1>
                {liaRef && <Badge variant="outline" className="text-slate-400 border-slate-600">{liaRef}</Badge>}
              </div>
              <p className="text-sm text-slate-400 mt-0.5">
                Complete all four parts of the LIA balancing test
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={cn('capitalize', STATUS_COLORS[form.status])}>
              {STATUS_OPTIONS.find(s => s.value === form.status)?.label || form.status}
            </Badge>
            {!isReadOnly && (
              <Button variant="outline" size="sm" onClick={() => handleSave()} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save
              </Button>
            )}
          </div>
        </div>

        {/* ── Progress ── */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-slate-400">
            <span>{STEPS[step].title}</span>
            <span>Step {step + 1} of {STEPS.length}</span>
          </div>
          <Progress value={progress} className="h-1.5 bg-slate-800" />
        </div>

        {/* ── Step Tabs ── */}
        <div className="flex gap-1 overflow-x-auto pb-2">
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => setStep(s.id)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all",
                  step === s.id
                    ? "bg-violet-600 text-white"
                    : "bg-slate-800/50 text-slate-400 hover:bg-slate-800 hover:text-white"
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {s.id === 0 ? 'Setup' : `Part ${s.id === 6 ? '4' : s.id <= 2 ? s.id : '3' + String.fromCharCode(96 + s.id - 2)}`}
              </button>
            );
          })}
        </div>

        {/* ── Step Content ── */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="border-b border-slate-800">
            <div className="flex items-center gap-3">
              {React.createElement(STEPS[step].icon, { className: "h-5 w-5 text-violet-400" })}
              <div>
                <CardTitle className="text-white">{STEPS[step].title}</CardTitle>
                <CardDescription>{STEPS[step].description}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">

            {/* ── Step 0: Setup ── */}
            {step === 0 && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label className="text-slate-200">Assessment Title <span className="text-rose-400">*</span></Label>
                    <Input
                      value={form.title}
                      onChange={e => handleChange('title', e.target.value)}
                      placeholder="e.g., LIA for Direct Marketing via Email"
                      className="bg-slate-900 border-slate-700"
                    />
                  </div>
                  <div className="space-y-2 col-span-2 sm:col-span-1">
                    <Label className="text-slate-200">Entity / Organisation</Label>
                    <Select value={form.entity_id || form.organization_id || ''} onValueChange={v => handleChange('entity_id', v)}>
                      <SelectTrigger className="bg-slate-900 border-slate-700">
                        <SelectValue placeholder="Select entity" />
                      </SelectTrigger>
                      <SelectContent>
                        {entities.map(e => (
                          <SelectItem key={e.id} value={e.id}>{e.entity_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 col-span-2 sm:col-span-1">
                    <Label className="text-slate-200">Status</Label>
                    <Select value={form.status} onValueChange={v => handleChange('status', v)}>
                      <SelectTrigger className="bg-slate-900 border-slate-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <QuestionField
                  label="Process Description"
                  guidance="Briefly describe the processing activity this LIA relates to."
                  field="process_description"
                  value={form.process_description}
                  onChange={handleChange}
                  rows={4}
                />
                <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20">
                  <p className="text-sm text-amber-400 font-medium mb-1">About Legitimate Interest</p>
                  <p className="text-xs text-slate-400">
                    Legitimate interest is the most flexible lawful basis for processing. It requires a three-part
                    balancing test: <strong className="text-slate-300">Purpose Test</strong> (is there a legitimate interest?),{' '}
                    <strong className="text-slate-300">Necessity Test</strong> (is the processing necessary?), and{' '}
                    <strong className="text-slate-300">Balancing Test</strong> (do individual interests override your interest?).
                    The DPO must complete Part 4.
                  </p>
                </div>
              </>
            )}

            {/* ── Step 1: Purpose Test ── */}
            {step === 1 && (
              <>
                <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 text-sm text-slate-300">
                  <strong>Purpose Test:</strong> You need to assess whether there is a legitimate interest behind the processing.
                </div>
                <QuestionField
                  label="1. Why do you want to process the data?"
                  field="purpose_why"
                  value={form.purpose_why}
                  onChange={handleChange}
                />
                <QuestionField
                  label="2. What benefit do you expect to get from the processing?"
                  field="purpose_benefit"
                  value={form.purpose_benefit}
                  onChange={handleChange}
                />
                <QuestionField
                  label="3. Do any third parties benefit from the processing?"
                  field="purpose_third_party_benefit"
                  value={form.purpose_third_party_benefit}
                  onChange={handleChange}
                />
                <QuestionField
                  label="4. Are there any wider public benefits to the processing?"
                  field="purpose_public_benefit"
                  value={form.purpose_public_benefit}
                  onChange={handleChange}
                />
                <QuestionField
                  label="5. How important are the benefits that you have identified?"
                  field="purpose_importance"
                  value={form.purpose_importance}
                  onChange={handleChange}
                />
                <QuestionField
                  label="6. What would the impact be if you could not go ahead with the processing?"
                  field="purpose_impact_without"
                  value={form.purpose_impact_without}
                  onChange={handleChange}
                />
                <QuestionField
                  label="7. Are you complying with other relevant laws?"
                  field="purpose_other_laws"
                  value={form.purpose_other_laws}
                  onChange={handleChange}
                />
                <QuestionField
                  label="8. Are you complying with industry guidelines or codes of practice?"
                  field="purpose_guidelines"
                  value={form.purpose_guidelines}
                  onChange={handleChange}
                />
              </>
            )}

            {/* ── Step 2: Necessity Test ── */}
            {step === 2 && (
              <>
                <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 text-sm text-slate-300">
                  <strong>Necessity Test:</strong> You need to assess whether the processing is necessary for the purpose you have identified.
                </div>
                <QuestionField
                  label="1. Will this processing help you achieve your purpose?"
                  field="necessity_helps_purpose"
                  value={form.necessity_helps_purpose}
                  onChange={handleChange}
                />
                <QuestionField
                  label="2. Is the processing proportionate to that purpose?"
                  field="necessity_proportionate"
                  value={form.necessity_proportionate}
                  onChange={handleChange}
                />
                <QuestionField
                  label="3. Can you achieve the same purpose without the processing?"
                  field="necessity_alternative"
                  value={form.necessity_alternative}
                  onChange={handleChange}
                />
                <QuestionField
                  label="4. Can you achieve the same purpose by processing less data, or by processing the data in another more obvious or less intrusive way?"
                  field="necessity_less_data"
                  value={form.necessity_less_data}
                  onChange={handleChange}
                />
              </>
            )}

            {/* ── Step 3: Nature of Personal Data ── */}
            {step === 3 && (
              <>
                <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 text-sm text-slate-300">
                  <strong>Balancing Test — Nature of Personal Data:</strong> Consider the impact on individuals' interests, rights and freedoms.
                </div>
                <QuestionField
                  label="1. Is it special category data or criminal offence data?"
                  guidance="Special categories include health, racial/ethnic origin, political opinions, religious beliefs, trade union membership, genetic/biometric data, sexual orientation."
                  field="nature_special_category"
                  value={form.nature_special_category}
                  onChange={handleChange}
                />
                <QuestionField
                  label="2. Is it data which people are likely to consider particularly 'private'?"
                  field="nature_private"
                  value={form.nature_private}
                  onChange={handleChange}
                />
                <QuestionField
                  label="3. Are you processing children's data or data relating to other vulnerable people?"
                  field="nature_vulnerable"
                  value={form.nature_vulnerable}
                  onChange={handleChange}
                />
                <QuestionField
                  label="4. Is the data about people in their personal or professional capacity?"
                  field="nature_capacity"
                  value={form.nature_capacity}
                  onChange={handleChange}
                />
              </>
            )}

            {/* ── Step 4: Reasonable Expectations ── */}
            {step === 4 && (
              <>
                <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 text-sm text-slate-300">
                  <strong>Balancing Test — Reasonable Expectations:</strong> Assess what individuals would reasonably expect.
                </div>
                <QuestionField
                  label="1. Do you have an existing relationship with the individual?"
                  field="expect_existing_relationship"
                  value={form.expect_existing_relationship}
                  onChange={handleChange}
                />
                <QuestionField
                  label="2. What's the nature of the relationship and how have you used data in the past?"
                  field="expect_relationship_nature"
                  value={form.expect_relationship_nature}
                  onChange={handleChange}
                />
                <QuestionField
                  label="3. Did you collect the data directly from the individual?"
                  field="expect_collected_directly"
                  value={form.expect_collected_directly}
                  onChange={handleChange}
                />
                <QuestionField
                  label="4. What did you tell them at the time?"
                  field="expect_told_at_time"
                  value={form.expect_told_at_time}
                  onChange={handleChange}
                />
                <QuestionField
                  label="5. How long ago did you collect the data?"
                  field="expect_how_long_ago"
                  value={form.expect_how_long_ago}
                  onChange={handleChange}
                />
                <QuestionField
                  label="6. Are there any changes in technology or context since then that would affect expectations?"
                  field="expect_tech_changes"
                  value={form.expect_tech_changes}
                  onChange={handleChange}
                />
                <QuestionField
                  label="7. Is your intended purpose and method widely understood?"
                  field="expect_widely_understood"
                  value={form.expect_widely_understood}
                  onChange={handleChange}
                />
                <QuestionField
                  label="8. Are you intending to do anything new or innovative?"
                  field="expect_new_innovative"
                  value={form.expect_new_innovative}
                  onChange={handleChange}
                />
                <QuestionField
                  label="9. Are there any other factors in the particular circumstances that mean they would or would not expect the processing?"
                  field="expect_other_factors"
                  value={form.expect_other_factors}
                  onChange={handleChange}
                />
              </>
            )}

            {/* ── Step 5: Likely Impact ── */}
            {step === 5 && (
              <>
                <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 text-sm text-slate-300">
                  <strong>Balancing Test — Likely Impact:</strong> Consider what impact the processing will have on individuals.
                </div>
                <QuestionField
                  label="1. What are the possible impacts of the processing on people?"
                  field="impact_possible"
                  value={form.impact_possible}
                  onChange={handleChange}
                />
                <QuestionField
                  label="2. Will individuals lose any control over the use of their personal data?"
                  field="impact_control_loss"
                  value={form.impact_control_loss}
                  onChange={handleChange}
                />
                <QuestionField
                  label="3. What is the likelihood and severity of any potential impact?"
                  field="impact_likelihood_severity"
                  value={form.impact_likelihood_severity}
                  onChange={handleChange}
                />
                <QuestionField
                  label="4. Are some people likely to object to the processing or find it intrusive?"
                  field="impact_objections"
                  value={form.impact_objections}
                  onChange={handleChange}
                />
                <QuestionField
                  label="5. Would you be happy to explain the processing to individuals?"
                  field="impact_happy_explain"
                  value={form.impact_happy_explain}
                  onChange={handleChange}
                />
                <QuestionField
                  label="6. Can you adopt any safeguards to minimise the impact?"
                  field="impact_safeguards"
                  value={form.impact_safeguards}
                  onChange={handleChange}
                />
                <QuestionField
                  label="7. Can you offer individuals an opt-out?"
                  field="impact_opt_out"
                  value={form.impact_opt_out}
                  onChange={handleChange}
                />
              </>
            )}

            {/* ── Step 6: DPO Decision ── */}
            {step === 6 && (
              <>
                <div className="p-3 rounded-lg bg-violet-500/5 border border-violet-500/20 text-sm text-slate-300">
                  <strong className="text-violet-400">Part 4 — Making the Decision</strong>
                  <p className="mt-1 text-slate-400">
                    This section is to be completed by the DPO together with the Business/Project Lead, using answers to Parts 1, 2 and 3 to decide whether you can apply the legitimate interests basis.
                  </p>
                </div>

                {/* Decision: can rely? */}
                <div className="space-y-3">
                  <Label className="text-slate-200 text-sm font-semibold">Can you rely on legitimate interests for this processing?</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: 'yes', label: 'Yes', icon: ThumbsUp, color: 'border-emerald-500 bg-emerald-500/10 text-emerald-400' },
                      { value: 'conditional', label: 'Conditional', icon: Minus, color: 'border-amber-500 bg-amber-500/10 text-amber-400' },
                      { value: 'no', label: 'No', icon: ThumbsDown, color: 'border-rose-500 bg-rose-500/10 text-rose-400' },
                    ].map(({ value, label, icon: Icon, color }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => handleChange('dpo_can_rely', value)}
                        className={cn(
                          "p-4 rounded-lg border-2 text-center transition-all",
                          form.dpo_can_rely === value ? color : "border-slate-700 hover:border-slate-600 text-slate-400"
                        )}
                      >
                        <Icon className="h-6 w-6 mx-auto mb-2" />
                        <p className="font-semibold text-sm">{label}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <QuestionField
                  label="DPO Rationale"
                  guidance="Explain the reasoning behind the decision, referencing specific answers from Parts 1–3."
                  field="dpo_rationale"
                  value={form.dpo_rationale}
                  onChange={handleChange}
                  rows={4}
                />

                {form.dpo_can_rely === 'conditional' && (
                  <QuestionField
                    label="Conditions / Safeguards Required"
                    guidance="Specify what conditions must be met or safeguards implemented before legitimate interests can be relied upon."
                    field="dpo_conditions"
                    value={form.dpo_conditions}
                    onChange={handleChange}
                    rows={3}
                  />
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-200 text-sm">LIA Completed by (Business/Project Lead)</Label>
                    <Input
                      value={form.completed_by}
                      onChange={e => handleChange('completed_by', e.target.value)}
                      placeholder="Full name"
                      className="bg-slate-900 border-slate-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-200 text-sm">Date</Label>
                    <Input
                      type="date"
                      value={form.completion_date}
                      onChange={e => handleChange('completion_date', e.target.value)}
                      className="bg-slate-900 border-slate-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-200 text-sm">DPO Name</Label>
                    <Input
                      value={form.dpo_name}
                      onChange={e => handleChange('dpo_name', e.target.value)}
                      placeholder="DPO full name"
                      className="bg-slate-900 border-slate-700"
                    />
                  </div>
                </div>

                <QuestionField
                  label="Comments"
                  guidance="Any additional comments or notes."
                  field="comments"
                  value={form.comments}
                  onChange={handleChange}
                  rows={3}
                />

                {/* Final submission */}
                <div className="pt-4 border-t border-slate-800">
                  <div className="flex gap-3 justify-end">
                    {!isReadOnly && (
                      <>
                        {form.dpo_can_rely === 'yes' && (
                          <Button
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handleSave('approved')}
                            disabled={saving}
                          >
                            <ThumbsUp className="h-4 w-4 mr-2" />
                            Finalise — Approved
                          </Button>
                        )}
                        {form.dpo_can_rely === 'no' && (
                          <Button
                            className="bg-rose-600 hover:bg-rose-700"
                            onClick={() => handleSave('rejected')}
                            disabled={saving}
                          >
                            <ThumbsDown className="h-4 w-4 mr-2" />
                            Finalise — Rejected
                          </Button>
                        )}
                        {form.dpo_can_rely === 'conditional' && (
                          <Button
                            className="bg-amber-600 hover:bg-amber-700"
                            onClick={() => handleSave('conditional')}
                            disabled={saving}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Finalise — Conditional
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* ── Navigation ── */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => setStep(s => Math.max(0, s - 1))}
            disabled={step === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-2" /> Previous
          </Button>
          <div className="flex gap-2">
            {!isReadOnly && (
              <Button variant="outline" onClick={() => handleSave()} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Draft
              </Button>
            )}
            {step < STEPS.length - 1 && (
              <Button
                className="bg-violet-600 hover:bg-violet-700"
                onClick={() => setStep(s => Math.min(STEPS.length - 1, s + 1))}
              >
                Next <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
